import React from "react";

const MARKET_DEPTH_THEME = {
  primary: "#2457BF",
  primaryDark: "#1A42A0",
  primarySoft: "#EFF4FF",
  primarySoftAlt: "#DCE8FF",
  primaryText: "#2457BF",
  primaryTint: "rgba(36, 87, 191, 0.04)",
  primaryTintStrong: "rgba(36, 87, 191, 0.08)",
};

export default function MarketDepthPopover({
  popoverRef,
  position = { top: 0, left: 0 },
  depthRows = [],
  onClose,
  tr = (key) => key,
  formatNumber = (num) => num,
}) {
  const theme = MARKET_DEPTH_THEME;
  const rows = Array.isArray(depthRows) ? depthRows : [];
  const bids = rows.filter((d) => d.BbBuySellFlag === 66);
  const asks = rows.filter((d) => d.BbBuySellFlag !== 66);

  return (
    <div
      ref={popoverRef}
      style={{
        position: "fixed",
        top: `${position.top}px`,
        left: `${position.left}px`,
        zIndex: 9999,
        width: "480px",
        maxHeight: "calc(100vh - 100px)",
        overflowY: "auto",
        overflowX: "hidden",
        backgroundColor: "#FFFFFF",
        borderRadius: "16px",
        boxShadow:
          "0 20px 60px rgba(0, 0, 0, 0.2), 0 0 1px rgba(0, 0, 0, 0.1)",
        animation: "fadeInScale 0.2s ease-out",
      }}
    >
      <div
        style={{
          position: "absolute",
          right: "-8px",
          top: "30px",
          width: 0,
          height: 0,
          borderTop: "8px solid transparent",
          borderBottom: "8px solid transparent",
          borderLeft: `8px solid ${theme.primary}`,
        }}
      />

      <div
        style={{
          background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.primaryDark} 100%)`,
          padding: "1.25rem 1.5rem",
          position: "relative",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <h5
          style={{
            color: "#FFFFFF",
            fontSize: "1rem",
            fontWeight: 700,
            letterSpacing: "-0.02em",
            margin: 0,
          }}
        >
          {tr("marketDepth")}
        </h5>
        <button
          type="button"
          onClick={onClose}
          style={{
            background: "rgba(255, 255, 255, 0.2)",
            border: "none",
            borderRadius: "6px",
            width: "28px",
            height: "28px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            transition: "all 0.2s",
            flexShrink: 0,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(255, 255, 255, 0.3)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)";
          }}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path
              d="M1 1L13 13M13 1L1 13"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>

      <div style={{ padding: "1.25rem" }}>
        <div
          style={{
            borderRadius: "12px",
            overflow: "hidden",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
            border: "1px solid #E8EAED",
          }}
        >
          <table
            style={{
              backgroundColor: "#FFFFFF",
              width: "100%",
              borderCollapse: "collapse",
            }}
          >
            <thead>
              <tr>
                <th
                  colSpan="3"
                  style={{
                    background: `linear-gradient(135deg, ${theme.primarySoft} 0%, ${theme.primarySoftAlt} 100%)`,
                    color: theme.primaryText,
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.03em",
                    padding: "0.75rem 0.4rem",
                    borderBottom: `2px solid ${theme.primary}`,
                    borderRight: "1px solid #E8EAED",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "0.3rem",
                    }}
                  >
                    <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M8 12V4M8 4L4 8M8 4L12 8"
                        stroke={theme.primary}
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <span>{tr("bid")}</span>
                  </div>
                </th>
                <th
                  colSpan="3"
                  style={{
                    background: `linear-gradient(135deg, ${theme.primarySoft} 0%, ${theme.primarySoftAlt} 100%)`,
                    color: theme.primaryText,
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.03em",
                    padding: "0.75rem 0.4rem",
                    borderBottom: `2px solid ${theme.primary}`,
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "0.3rem",
                    }}
                  >
                    <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M8 4V12M8 12L12 8M8 12L4 8"
                        stroke={theme.primary}
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <span>{tr("ask")}</span>
                  </div>
                </th>
              </tr>
              <tr style={{ backgroundColor: "#F8F9FB" }}>
                {["qtyShort", "price", "ord", "qtyShort", "price", "ord"].map(
                  (labelKey, index) => (
                    <th
                      key={`${labelKey}-${index}`}
                      style={{
                        fontSize: "0.6rem",
                        fontWeight: 600,
                        color: "#374151",
                        textTransform: "uppercase",
                        letterSpacing: "0.03em",
                        padding: "0.6rem 0.25rem",
                        borderBottom: "1px solid #E8EAED",
                        borderRight: index < 5 ? "1px solid #E8EAED" : undefined,
                        textAlign: "center",
                        width: "16.66%",
                      }}
                    >
                      {tr(labelKey)}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {[...Array(5)].map((_, i) => {
                const bid = bids[i] || {
                  Quantity: 0,
                  Price: 0,
                  NumberOfOrders: 0,
                };
                const ask = asks[i] || {
                  Quantity: 0,
                  Price: 0,
                  NumberOfOrders: 0,
                };

                return (
                  <tr
                    key={i}
                    style={{
                      transition: "all 0.2s ease",
                      borderBottom: i < 4 ? "1px solid #F3F4F6" : "none",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#F9FAFB";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }}
                  >
                    <td
                      style={{
                        fontWeight: 600,
                        color: theme.primaryText,
                        fontSize: "0.75rem",
                        padding: "0.6rem 0.25rem",
                        borderRight: "1px solid #F3F4F6",
                        backgroundColor: theme.primaryTint,
                        textAlign: "center",
                        width: "16.66%",
                      }}
                    >
                      {bid.Quantity ? bid.Quantity : "0"}
                    </td>
                    <td
                      style={{
                        fontWeight: 700,
                        color: theme.primaryText,
                        fontSize: "0.8rem",
                        padding: "0.6rem 0.25rem",
                        borderRight: "1px solid #F3F4F6",
                        backgroundColor: theme.primaryTintStrong,
                        textAlign: "center",
                        width: "16.66%",
                      }}
                    >
                      {bid.Price ? formatNumber(bid.Price) : "0"}
                    </td>
                    <td
                      style={{
                        fontWeight: 600,
                        color: theme.primaryText,
                        fontSize: "0.7rem",
                        padding: "0.6rem 0.25rem",
                        borderRight: "1px solid #F3F4F6",
                        backgroundColor: theme.primaryTint,
                        textAlign: "center",
                        width: "16.66%",
                      }}
                    >
                      <span
                        style={{
                          backgroundColor: theme.primarySoft,
                          padding: "0.15rem 0.2rem",
                          borderRadius: "4px",
                          fontSize: "0.6rem",
                          fontWeight: 700,
                          display: "inline-block",
                        }}
                      >
                        {bid.NumberOfOrders ? bid.NumberOfOrders : "0"}
                      </span>
                    </td>
                    <td
                      style={{
                        fontWeight: 600,
                        color: theme.primaryText,
                        fontSize: "0.75rem",
                        padding: "0.6rem 0.25rem",
                        borderRight: "1px solid #F3F4F6",
                        backgroundColor: theme.primaryTint,
                        textAlign: "center",
                        width: "16.66%",
                      }}
                    >
                      {ask.Quantity ? ask.Quantity : "0"}
                    </td>
                    <td
                      style={{
                        fontWeight: 700,
                        color: theme.primaryText,
                        fontSize: "0.8rem",
                        padding: "0.6rem 0.25rem",
                        borderRight: "1px solid #F3F4F6",
                        backgroundColor: theme.primaryTintStrong,
                        textAlign: "center",
                        width: "16.66%",
                      }}
                    >
                      {ask.Price ? formatNumber(ask.Price) : "0"}
                    </td>
                    <td
                      style={{
                        fontWeight: 600,
                        color: theme.primaryText,
                        fontSize: "0.7rem",
                        padding: "0.6rem 0.25rem",
                        backgroundColor: theme.primaryTint,
                        textAlign: "center",
                        width: "16.66%",
                      }}
                    >
                      <span
                        style={{
                          backgroundColor: theme.primarySoft,
                          padding: "0.15rem 0.2rem",
                          borderRadius: "4px",
                          fontSize: "0.6rem",
                          fontWeight: 700,
                          display: "inline-block",
                        }}
                      >
                        {ask.NumberOfOrders > 0 ? ask.NumberOfOrders : "0"}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div
          style={{
            marginTop: "1rem",
            padding: "0.75rem 1rem",
            backgroundColor: "#F8F9FB",
            borderRadius: "10px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontSize: "0.65rem",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
            <div
              style={{
                width: "6px",
                height: "6px",
                borderRadius: "50%",
                backgroundColor: theme.primary,
              }}
            />
            <span style={{ color: "#6B7280", fontWeight: 600 }}>{tr("buy")}</span>
          </div>
          <div style={{ color: "#9CA3AF", fontWeight: 500 }}>{tr("liveData")}</div>
          <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
            <div
              style={{
                width: "6px",
                height: "6px",
                borderRadius: "50%",
                backgroundColor: theme.primary,
              }}
            />
            <span style={{ color: "#6B7280", fontWeight: 600 }}>{tr("sell")}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
